import { useState } from "react";

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  if (dateStr.match(/^\d{2}-\d{2}-\d{4}$/)) return dateStr;
  if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = dateStr.split('-');
    return `${day}-${month}-${year}`;
  }
  try {
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    }
  } catch {
    return dateStr;
  }
  return dateStr;
};

const WeatherAnalysisSection = ({ weatherAnalysis, tripSummary }) => {
  const [showAllDays, setShowAllDays] = useState(false);

  if (!weatherAnalysis) {
    return null;
  }

  const {
    summary,
    temperature,
    conditions,
    rainfall,
    humidity,
    bestTimeToVisit,
    recommendations,
    dailyForecast
  } = weatherAnalysis;

  const getBadgeStyle = () => {
    const text = bestTimeToVisit?.toLowerCase() || '';
    if (text.includes('excellent') || text.includes('peak') || text.includes('best')) {
      return 'bg-green-100 text-green-700 border-green-200';
    }
    if (text.includes('good') || text.includes('great')) {
      return 'bg-blue-100 text-blue-700 border-blue-200';
    }
    if (text.includes('fair') || text.includes('moderate')) {
      return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    }
    return 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const getRainfallStyle = () => {
    const level = rainfall?.toLowerCase() || '';
    if (level.includes('low') || level.includes('minimal')) {
      return { color: 'text-green-600', bg: 'bg-green-50', icon: '💧' };
    }
    if (level.includes('high') || level.includes('heavy')) {
      return { color: 'text-blue-600', bg: 'bg-blue-50', icon: '🌧️' };
    }
    return { color: 'text-yellow-600', bg: 'bg-yellow-50', icon: '💦' };
  };

  const rainfallStyle = getRainfallStyle();
  const visibleForecast = showAllDays ? dailyForecast : dailyForecast?.slice(0, 5);

  return (
    <div className="bg-white rounded-2xl shadow-sm mb-6 border border-gray-100 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-1.5 h-full bg-gradient-to-b from-sky-400 to-blue-600 rounded-l-2xl"></div>
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-sky-100 rounded-xl flex items-center justify-center">
              <span className="text-2xl">🌤️</span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Weather Analysis</h2>
              <p className="text-sm text-gray-500">
                {/* A copied-but-not-yet-dated trip has no startDate/endDate — show
                    just the destination rather than "• to " with nothing between. */}
                {tripSummary?.destination}
                {tripSummary?.startDate && tripSummary?.endDate &&
                  ` • ${formatDate(tripSummary.startDate)} to ${formatDate(tripSummary.endDate)}`}
              </p>
            </div>
          </div>
          {bestTimeToVisit && (
            <span className={`px-3 py-1.5 rounded-full text-sm font-medium border ${getBadgeStyle()}`}>
              {bestTimeToVisit}
            </span>
          )}
        </div>
      </div>

      {summary && (
        <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-sky-50 border-b border-blue-100">
          <p className="text-[15px] sm:text-base text-gray-700 leading-relaxed tracking-wide font-medium">{summary}</p>
        </div>
      )}

      <div className="p-6">

        {dailyForecast && dailyForecast.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Daily Forecast
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {visibleForecast.map((day, idx) => (
                <div
                  key={idx}
                  className="bg-gradient-to-br from-gray-50 to-white border border-gray-100 rounded-xl p-3 text-center hover:shadow-md transition-shadow"
                >
                  <div className="text-xs font-semibold text-teal-600 mb-1">{day.day}</div>
                  {day.date && <div className="text-xs text-gray-400 mb-2">{formatDate(day.date)}</div>}
                  <div className="text-3xl mb-2">{day.icon || '🌤️'}</div>
                  <div className="text-xs text-gray-600 mb-1">{day.condition}</div>
                  <div className="flex justify-center gap-2 text-xs">
                    <span className="text-red-500 font-medium">{day.tempHigh}</span>
                    <span className="text-gray-300">/</span>
                    <span className="text-blue-500 font-medium">{day.tempLow}</span>
                  </div>
                </div>
              ))}
            </div>
            {dailyForecast.length > 5 && (
              <button
                onClick={() => setShowAllDays(!showAllDays)}
                className="mt-3 text-sm text-teal-600 hover:text-teal-700 font-medium cursor-pointer"
              >
                {showAllDays ? 'Show less' : `Show all ${dailyForecast.length} days`}
              </button>
            )}
          </div>
        )}

        {recommendations && recommendations.length > 0 && (
          <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl p-4 border border-teal-100">
            <h3 className="text-sm font-semibold text-teal-800 mb-3 flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              Weather Tips
            </h3>
            <ul className="space-y-2">
              {recommendations.map((rec, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="text-teal-500 mt-0.5">•</span>
                  <span>{rec}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default WeatherAnalysisSection;
